import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import SearchBar from './SearchBar';
import FavoritesMenu from './FavoritesMenu';

const navLinks = [
  { to: '/', label: '首页' },
  { to: '/about', label: '关于我们' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const categoryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:text-primary-600'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-book-open-line text-lg"></i>
          </span>
          <span className="hidden flex-col leading-none sm:flex">
            <span className="text-base font-bold text-foreground-950">双女主韩漫</span>
            <span className="font-label text-[10px] tracking-wide text-foreground-400">
              FREE ONLINE COMICS
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
          <div className="relative" ref={categoryRef}>
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                categoryOpen ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              分类浏览
              <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {categoryOpen && (
              <div className="absolute left-0 top-12 z-50 w-[420px] rounded-lg border border-background-200 bg-background-50 p-3">
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/category/${category.slug}`}
                      className="flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className={`${category.icon} text-lg`}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-900">
                          {category.name}
                        </span>
                        <span className="line-clamp-1 block text-[11px] text-foreground-400">
                          {category.description}
                        </span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="ml-auto hidden max-w-xs flex-1 md:block lg:max-w-sm">
          <SearchBar />
        </div>

        <div className="ml-auto flex items-center gap-2 md:ml-0">
          <div className="hidden md:block">
            <FavoritesMenu />
          </div>
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 lg:hidden cursor-pointer"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="flex flex-col gap-3 px-4 py-4">
            <SearchBar />
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-medium ${
                      isActive ? 'bg-primary-100 text-primary-700' : 'text-foreground-700'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
            <div>
              <p className="mb-2 px-3 text-xs font-bold uppercase tracking-wide text-foreground-400">
                分类浏览
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`/category/${category.slug}`}
                    className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2 text-sm text-foreground-800"
                  >
                    <i className={`${category.icon} text-accent-600`}></i>
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2">
              <span className="text-sm text-foreground-700">我的收藏</span>
              <FavoritesMenu />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}